import type { Lesson, Marathon, Module } from '@/api/courses';
import type { MarathonProgress } from '@/api/progress';

/**
 * Карточки «Мои марафоны» (D23, 11.09.2026) — всё, что карточка решает, без React.
 *
 * ⚠️ ДВУХ ВЕЩЕЙ В ДАННЫХ НЕТ, ОНИ ВЫВОДЯТСЯ ЗДЕСЬ.
 * - Тип курса. Поля «марафон / мини-курс» у курса нет: `subtype` — это поток и
 *   уровень, `access_category` — формат занятий. Мини-курсом в школе называют
 *   всё, что идёт в тарифе к основному курсу, поэтому тип = `is_primary`
 *   записи. Стенд 11.09: у 1439 из 1440 учеников с курсами основной ровно один.
 *   ⚠️ Открытому курсу БЕЗ записи (`enrolled === false`) метки нет вовсе (02.10.2026): «Мини-курс» — слово про
 *   курс из тарифа, а новичок без тарифа видел его на каждой карточке.
 * - «Завершён». Статуса «завершён» у записи нет (есть active, expired, …), а
 *   истёкшие курсы в список не приходят вовсе — дерево курса отдаёт 403.
 *   Завершённым считаем курс, где пройдены все уроки.
 */

export type MarathonCardSource = Pick<Marathon, 'id' | 'title' | 'cover_url'> & {
  is_primary?: boolean;
  /** `false` — открытый курс без записи; метку типа ему не рисуем. */
  enrolled?: boolean;
};

export type MarathonCardProgress = Pick<MarathonProgress, 'lessons_total' | 'lessons_completed' | 'percent_complete'>;

export type MarathonCardData = {
  id: string;
  title: string;
  /** null — обложки нет, карточка рисует букву. */
  coverUrl: string | null;
  initial: string;
  /** null — открытый курс без записи: метки типа нет. */
  typeKey: 'myMarathons.typeMarathon' | 'myMarathons.typeMiniCourse' | null;
  /** null — прогресс ещё не пришёл. Не 0: ноль ученик прочтёт как «ничего не сделано». */
  percent: number | null;
  done: number | null;
  total: number | null;
  completed: boolean;
  enterTo: string;
  statsTo: string;
};

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** Первая буква или цифра названия — для заглушки вместо обложки. */
export function titleInitial(title: string): string {
  const m = (title ?? '').match(/[\p{L}\p{N}]/u);
  return m ? m[0].toLocaleUpperCase() : '•';
}

/**
 * «Урок N из M» в блоке «Продолжить обучение» — главная и страница курса (11.09.2026).
 *
 * N — урок, на котором ученик СЕЙЧАС, а не число пройденных: «Урок 0 из 5» не по-русски
 * и не по-английски. Всё пройдено — последний урок, а не «6 из 5». M — `lessons_total`
 * из прогресса; пока он не пришёл — запасное число (уроки дерева, `lesson_count`).
 * Уроков нет — «0 из 0». Раньше формула была написана в `StudentDashboardPage` дважды.
 */
export function lessonPosition(
  progress: Pick<MarathonProgress, 'lessons_total' | 'lessons_completed'> | null | undefined,
  fallbackTotal: number,
): { number: number; total: number } {
  const total = Math.max(0, Math.floor(progress ? num(progress.lessons_total) : num(fallbackTotal)));
  const completed = progress ? Math.max(0, Math.floor(num(progress.lessons_completed))) : 0;
  return { number: Math.min(total, completed + 1), total };
}

/**
 * Урок, где ученик остановился, — для «Продолжить» в блоке на странице курса.
 *
 * Первый открытый урок по порядку дерева, как «текущий» в списке уроков; у старого
 * ответа без `access_status` открытость — по `is_available`. Нет такого (всё
 * пройдено или всё закрыто расписанием) — `null`: закрытый урок был бы тупиком.
 *
 * ⚠️ До 11.09.2026 этот расчёт вёл «Войти» на карточке прямо в урок. Замечание
 * владельца: «Войти» — сначала список уроков курса, продолжение — кнопкой там.
 */
export function continueLesson(modules: Module[] | undefined): Pick<Lesson, 'id' | 'title'> | null {
  const next = (modules ?? [])
    .flatMap((mod) => mod.lessons ?? [])
    .find((l) => (l.access_status ?? (l.is_available ? 'available' : 'locked')) === 'available');
  return next ? { id: next.id, title: next.title } : null;
}

/**
 * Что сказать на странице курса, в котором нет ни одного урока (02.10.2026, сверка пути бесплатника, Н2).
 *
 * «Обучение ещё не началось» — правда только про курс С ЗАПИСЬЮ, поток которого ещё не стартовал. Сервер считает
 * «ждущим начала» (`awaiting_start`) и бесплатника — у него нет записи на платный курс, — поэтому открытый курс
 * без уроков говорил новичку «ещё не началось», и это «скоро», которого у него быть не должно (D74).
 * Открытому курсу без записи (`enrolled === false`) — всегда просто «нет уроков».
 */
/**
 * Действующая запись на курс: `active` или `pending` (ждёт старта потока).
 *
 * ⚠️ ИСТЁКШАЯ И ОТМЕНЁННАЯ — НЕ ЗАПИСЬ (стенд 02.10.2026). Открытый курс виден и тому, у кого запись на него
 * когда-то была и кончилась: сервер отдаёт её в списке со статусом `expired` / `cancelled`. Первая версия считала
 * записью любую строку списка — и бывшему ученику на открытом курсе снова рисовалась метка «Мини-курс».
 */
export function isLiveEnrollment(status: string | undefined | null): boolean {
  return status === 'active' || status === 'pending';
}

export function emptyCourseKey(
  awaitingStart: boolean,
  enrolled: boolean | undefined,
): 'features.notStartedTitle' | 'lessons.noLessons' {
  return awaitingStart && enrolled !== false ? 'features.notStartedTitle' : 'lessons.noLessons';
}

export function marathonCard(m: MarathonCardSource, progress: MarathonCardProgress | null | undefined): MarathonCardData {
  const coursePage = `/app/lessons/marathon/${m.id}`;

  const cover = m.cover_url?.trim();

  let percent: number | null = null;
  let done: number | null = null;
  let total: number | null = null;
  if (progress) {
    percent = Math.min(100, Math.max(0, Math.round(num(progress.percent_complete))));
    done = Math.max(0, Math.floor(num(progress.lessons_completed)));
    total = Math.max(0, Math.floor(num(progress.lessons_total)));
  }

  return {
    id: m.id,
    title: m.title,
    coverUrl: cover ? cover : null,
    initial: titleInitial(m.title),
    typeKey:
      m.enrolled === false
        ? null
        : m.is_primary
          ? 'myMarathons.typeMarathon'
          : 'myMarathons.typeMiniCourse',
    percent,
    done,
    total,
    // ⚠️ `total > 0` обязателен: курс без уроков («0 из 0») иначе считался бы
    // пройденным и молча уезжал вниз, в завершённые.
    completed: total !== null && done !== null && total > 0 && done >= total,
    // «Войти» — в список уроков курса (замечание владельца 11.09.2026). Урок, где
    // ученик остановился, открывает «Продолжить» на странице курса — `continueLesson`.
    enterTo: coursePage,
    statsTo: coursePage,
  };
}

/** Активные сверху, завершённые ниже; внутри групп — порядок сервера (основной первым). */
export function splitMarathons<T extends { completed: boolean }>(cards: T[]): { active: T[]; completed: T[] } {
  return {
    active: cards.filter((c) => !c.completed),
    completed: cards.filter((c) => c.completed),
  };
}
